import React, {ChangeEvent, MutableRefObject, useEffect, useRef, useState} from 'react';
import {useHistory, useParams} from 'react-router-dom';
import classNames from 'classnames';
import {History} from 'history';
import {curry} from 'ramda';
import wrap from 'wraptile';
import {TabContext, TabPanel} from '@material-ui/lab';
import {AppBar, Tab, Tabs, CircularProgress} from '@material-ui/core';
import {ApTimelineBasic} from '@accurate-player/accurate-player-components-external';
import useQuery from '@pluto/query-hook';
import {formQuery} from '@pluto/rest';
import VideoAudioQC from './VideoAudioQC/VideoAudioQCConnected';
import ContentItemViewMetadata from './ContentItemViewMetadata/ContentItemViewMetadataConnected';
import ContentItemViewPublishingRightsContainer from './ContentItemViewPublishingRights/ContentItemViewPublishingRightsConnected';
import ContentItemViewArtwork from './ContentItemViewArtwork/ContentItemViewArtworkConnected';
import Divider from './Divider';
import {handleFetchQcMetadata} from './ContentItemViewMetadata/ContentItemViewMetadataContainer';
import {HandleChange, SetCurrentTab, TabsEnum} from './ContentItemViewTypes';
import {QcJobStatusIndicatorStateItem} from './JobStatusIndicator/JobStatusIndicatorTypes';
import {
    Item,
    ResetItemAction,
    SetItemAction,
} from './VideoAudioQC/QCMedia/Redux/QCMediaActions';
import {
    PlayerRef,
    ProgressivePlayerOrNull,
} from './VideoAudioQC/AccuratePlayer/AccuratePlayerTypes';
import QCTitleContainer from './ContentItemViewTitles/ContentItemViewTitlesContainer';
import ContentItemViewWorkHistoryContainer from './ContentItemViewWorkHistory/ContentItemViewWorkHistoryContainer';
import './ContentItemView.scss';
import ConfirmDialog from '../@pluto/delete-dialog/DeleteDialog';
import {handleModalState} from '../@pluto/utils';
import {useSelector} from 'react-redux';
import {selectContentItemViewMetadataDraft} from './ContentItemViewMetadata/Redux/ContentItemViewMetadataSelectors';

export interface HandleChangeParams {
    setCurrentTab: SetCurrentTab;
    playerRef: PlayerRef;
    history: History;
    id: string | null;
    title: string | null;
    isChangingTabAllowedRef: MutableRefObject<boolean>;
    setModalVisible: (value: boolean) => void;
    setTabToBeSentTo: (value: TabsEnum) => void;
    currentTab: TabsEnum | string;
}

export interface ContentItemViewContainerProps {
    item: Item | null;
    resetItem: ResetItemAction;
    setQcJobState: (id: number) => void;
    statusIndicators: QcJobStatusIndicatorStateItem[];
    setQcMetadata: (id: number) => void;
    resetQcMetadata: () => void;
    resetQcJobState: () => void;
    isLoading: boolean;
    isMetadataLoading: boolean;
    contentType: string | undefined;
    deliveryStartedAt: string;
    isDeliveryStarted: boolean;
    resetJobDeliveryStatus: () => void;
    getDeliveryJobStatus: (qcJobId: number) => void;
    deliveryJob: (qcJobId: number) => void;
    setItem: SetItemAction;
}

export const handleFetchStatuses = wrap(
    (setQcJobState: (value: number) => void, id: number) => {
        setQcJobState(id);
    }
);

export const getStatusByType = (
    statusIndicators: QcJobStatusIndicatorStateItem[],
    type: string
) => {
    if (!statusIndicators) return null;

    const statusValue = statusIndicators.filter(
        (status: QcJobStatusIndicatorStateItem) => status.type === type
    );

    return statusValue[0];
};

export const handleChange = curry(
    (params: HandleChangeParams, event: ChangeEvent<{}>, newValue: TabsEnum) => {
        const {
            setCurrentTab,
            history,
            isChangingTabAllowedRef,
            setModalVisible,
            setTabToBeSentTo,
            playerRef,
            currentTab,
        } = params;
        const queryParams = {
            tab: newValue,
        };
        const search = formQuery('', queryParams);

        if (isChangingTabAllowedRef.current) {
            if (playerRef?.current && currentTab === TabsEnum.visualQc) {
                playerRef.current.api.pause();
            }

            setCurrentTab(newValue);
            history.push({
                pathname: history.location.pathname,
                search,
            });
        } else {
            setModalVisible(true);
            setTabToBeSentTo(newValue);
        }
    }
);

export const getInitialTabValue = (tabValue: TabsEnum | undefined): TabsEnum | string =>
    tabValue || TabsEnum.none;

export const handleUnmount = wrap((params: any) => {
    const {resetQcMetadata, resetQcJobState, resetJobDeliveryStatus, resetItem} = params;
    resetQcMetadata();
    resetQcJobState();
    resetJobDeliveryStatus();
    resetItem();
});

export const fetchData = wrap((params: any) => {
    const {
        onHandleUnmountEffect,
        fetchStatuses,
        fetchDeliveryJobStatus,
        fetchMetadata,
        setItem,
        id,
    } = params;
    fetchStatuses();
    fetchDeliveryJobStatus();
    fetchMetadata();
    setItem(id);

    return onHandleUnmountEffect;
});

export const onConfirmConfirmationDialog = wrap(
    (
        setModalVisible: (value: boolean) => void,
        tabToBeSentTo: TabsEnum,
        setCurrentTab: (value: TabsEnum) => void,
        history: History,
        setIsGeneralMetadataEditMode: (value: boolean) => void,
        setIsLocalizedMetadataEditMode: (value: boolean) => void,
        setIsTerritorialMetadataEditMode: (value: boolean) => void
    ) => {
        const queryParams = {
            tab: tabToBeSentTo,
        };
        const search = formQuery('', queryParams);
        history.push({
            pathname: history.location.pathname,
            search,
        });
        setCurrentTab(tabToBeSentTo);
        setModalVisible(false);
        setIsGeneralMetadataEditMode(false);
        setIsLocalizedMetadataEditMode(false);
        setIsTerritorialMetadataEditMode(false);
    }
);

export const onCloseConfirmDialog = wrap((setModalVisible: (value: boolean) => void) => {
    setModalVisible(false);
});

export const getConfirmationDialog = (
    modalVisible: boolean,
    setModalVisible: (value: boolean) => void,
    tabToBeSentTo: TabsEnum,
    setCurrentTab: (value: TabsEnum) => void,
    history: History,
    setIsGeneralMetadataEditMode: (value: boolean) => void,
    setIsLocalizedMetadataEditMode: (value: boolean) => void,
    setIsTerritorialMetadataEditMode: (value: boolean) => void
) => {
    return modalVisible ? (
        <ConfirmDialog
            onClose={onCloseConfirmDialog(setModalVisible)}
            onConfirm={onConfirmConfirmationDialog(
                setModalVisible,
                tabToBeSentTo,
                setCurrentTab,
                history,
                setIsGeneralMetadataEditMode,
                setIsLocalizedMetadataEditMode,
                setIsTerritorialMetadataEditMode
            )}
            text="Are you sure you want to leave this page?"
            secondaryText="Changes you made will not be saved"
            cancelText="STAY"
            confirmText="LEAVE"
        />
    ) : null;
};

export const handleConfirmDeliveryJob = wrap(
    (
        deliveryJob: (qcJobId: number) => void,
        setModalVisible: (value: boolean) => void,
        qcJobId: number
    ) => {
        setModalVisible(false);
        deliveryJob(qcJobId);
    }
);

export const getDeliveryJobConfirmationDialog = (
    modalVisible: boolean,
    setModalVisible: (value: boolean) => void,
    confirmDeliveryJob: () => void
) => {
    return modalVisible ? (
        <ConfirmDialog
            onClose={handleModalState(setModalVisible, false)}
            onConfirm={confirmDeliveryJob}
            text="You changed metadata, do you want to add this item to the delivery queue?"
            secondaryText=""
            cancelText="No"
            confirmText="Yes"
            additionalClassName="delivery-job-popup"
        />
    ) : null;
};

export const onChangingTabAllowedRefEffect = wrap(
    (
        isChangingTabAllowedRef: MutableRefObject<boolean>,
        isGeneralEditMode: boolean,
        isLocalizedEditMode: boolean,
        isTerritorialMetadataEditMode: boolean
    ) => {
        isChangingTabAllowedRef.current =
            !isGeneralEditMode && !isLocalizedEditMode && !isTerritorialMetadataEditMode;
    }
);

export const handleSettingCurrentTab = wrap(
    (
        contentType: string | undefined,
        setCurrentTab: (tab: TabsEnum) => void,
        tabValue: TabsEnum
    ) => {
        if (contentType) {
            if (contentType !== 'series') {
                setCurrentTab(tabValue || TabsEnum.visualQc);
            } else {
                setCurrentTab(tabValue || TabsEnum.metadata);
            }
        }
    }
);

export const handleDeliveryJob = wrap(
    (deliveryJob: (qcJobId: string) => void, qcJobId: string) => {
        deliveryJob(qcJobId);
    }
);

export const getJobDeliveryButton = (
    onClickToDeliveryQueue: () => void,
    isDraft: boolean
) => {
    return (
        <button
            type="button"
            onClick={onClickToDeliveryQueue}
            className="qc-app-bar__delivery-button"
            disabled={!isDraft}
        >
            ADD TO DELIVERY QUEUE
        </button>
    );
};

export const getIsAllTabsPassed = (statusIndicators: QcJobStatusIndicatorStateItem[]) => {
    return (
        Boolean(statusIndicators.length) &&
        !statusIndicators.filter(
            (item) => item.status !== 'PASSED' && item.type !== 'validation'
        ).length
    );
};

export const handleStatusIndicatorsChange = wrap(
    (
        jobDeliveryDate: string,
        isAllTabsPassed: boolean,
        setIsDeliveryPopupOpen: (value: boolean) => void,
        qcJobId: string
    ) => {
        const deliveredJobIds: string = localStorage.getItem('deliveredJobIds') || '{}';
        const newDeliveredJobIds = JSON.parse(deliveredJobIds);

        if (
            !jobDeliveryDate &&
            isAllTabsPassed &&
            !JSON.parse(deliveredJobIds)[qcJobId]
        ) {
            setIsDeliveryPopupOpen(true);
            newDeliveredJobIds[qcJobId] = true;
            localStorage.setItem('deliveredJobIds', JSON.stringify(newDeliveredJobIds));
        }
    }
);

export const handleFetchDeliveryJob = wrap(
    (getDeliveryJobStatus: (qcJobId: number) => void, qcJobId: number) => {
        getDeliveryJobStatus(qcJobId);
    }
);

export const handleJobDeliveryChange = wrap(
    (
        jobDeliveryDate: string,
        setIsGeneralMetadataEditMode: (value: boolean) => {},
        setIsLocalizedMetadataEditMode: (value: boolean) => {},
        setIsTerritorialMetadataEditMode: (value: boolean) => {}
    ) => {
        if (Boolean(jobDeliveryDate)) {
            setIsGeneralMetadataEditMode(false);
            setIsLocalizedMetadataEditMode(false);
            setIsTerritorialMetadataEditMode(false);
        }
    }
);

export default function ContentItemViewContainer(props: ContentItemViewContainerProps) {
    const {
        setQcJobState,
        statusIndicators,
        setQcMetadata,
        resetQcMetadata,
        isLoading,
        isMetadataLoading,
        contentType,
        deliveryStartedAt: jobDeliveryDate,
        deliveryJob,
        getDeliveryJobStatus,
        resetJobDeliveryStatus,
        resetQcJobState,
        setItem,
        resetItem,
        item,
    } = props;

    const contentItemViewMetadataDraft = useSelector(selectContentItemViewMetadataDraft);
    const [modalVisible, setModalVisible] = useState(false);
    const [isDeliveryPopupOpen, setIsDeliveryPopupOpen] = useState(
        contentItemViewMetadataDraft
    );
    const [isGeneralMetadataEditMode, setIsGeneralMetadataEditMode] = useState<boolean>(
        false
    );
    const [
        isLocalizedMetadataEditMode,
        setIsLocalizedMetadataEditMode,
    ] = useState<boolean>(false);
    const [
        isTerritorialMetadataEditMode,
        setIsTerritorialMetadataEditMode,
    ] = useState<boolean>(false);
    const [tabToBeSentTo, setTabToBeSentTo] = useState<TabsEnum>(TabsEnum.visualQc);

    const isChangingTabAllowedRef = useRef<boolean>(false);
    const reloadRef = useRef<boolean>(false);
    const {id} = useParams<{id: string}>();
    const {title} = useParams<{title: string}>();
    const fetchStatuses = handleFetchStatuses(setQcJobState, id, isLoading);
    const fetchMetadata = handleFetchQcMetadata(
        setQcMetadata,
        resetQcMetadata,
        id,
        isMetadataLoading
    );
    const fetchDeliveryJobStatus = handleFetchDeliveryJob(getDeliveryJobStatus, id);
    const onClickAddToDeliveryQueue = handleDeliveryJob(deliveryJob, id);
    const onHandleUnmountEffect = handleUnmount({
        resetQcMetadata,
        resetQcJobState,
        resetJobDeliveryStatus,
        resetItem,
    });

    useEffect(
        fetchData({
            fetchStatuses,
            fetchMetadata,
            fetchDeliveryJobStatus,
            onHandleUnmountEffect,
            setItem,
            id,
        }),
        []
    );
    useEffect(
        handleJobDeliveryChange(
            jobDeliveryDate,
            setIsGeneralMetadataEditMode,
            setIsLocalizedMetadataEditMode,
            setIsTerritorialMetadataEditMode
        ),
        [jobDeliveryDate]
    );

    useEffect(
        onChangingTabAllowedRefEffect(
            isChangingTabAllowedRef,
            isGeneralMetadataEditMode,
            isLocalizedMetadataEditMode,
            isTerritorialMetadataEditMode
        ),
        [
            isGeneralMetadataEditMode,
            isLocalizedMetadataEditMode,
            isTerritorialMetadataEditMode,
        ]
    );

    const history = useHistory();

    const query = useQuery();
    const tabValue = query.get('tab') as TabsEnum;
    const initialTabValue = getInitialTabValue(tabValue);
    const [currentTab, setCurrentTab] = useState<TabsEnum | string>(initialTabValue);
    const playerRef = useRef<ProgressivePlayerOrNull>(null);
    const timelineRef = useRef<ApTimelineBasic | null>(null);
    const onHandleChange: HandleChange = handleChange({
        playerRef,
        setCurrentTab,
        currentTab,
        history,
        id,
        title,
        isChangingTabAllowedRef,
        setModalVisible,
        setTabToBeSentTo,
    });

    useEffect(handleSettingCurrentTab(contentType, setCurrentTab, tabValue), [
        contentType,
        tabValue,
    ]);

    const isAllTabsPassed = getIsAllTabsPassed(statusIndicators);

    useEffect(
        handleStatusIndicatorsChange(
            jobDeliveryDate,
            isAllTabsPassed,
            setIsDeliveryPopupOpen,
            id
        ),
        [isAllTabsPassed, jobDeliveryDate]
    );

    useEffect(() => {
        setIsDeliveryPopupOpen(contentItemViewMetadataDraft);
    }, [contentItemViewMetadataDraft]);

    const confirmDeliveryJob = handleConfirmDeliveryJob(
        deliveryJob,
        setIsDeliveryPopupOpen,
        id
    );
    const isVisualQcCurrentTab = currentTab === TabsEnum.visualQc;

    if (!contentType) {
        return <CircularProgress />;
    }

    return (
        <div className="tools-container">
            {getDeliveryJobConfirmationDialog(
                isDeliveryPopupOpen,
                setIsDeliveryPopupOpen,
                confirmDeliveryJob
            )}
            {getConfirmationDialog(
                modalVisible,
                setModalVisible,
                tabToBeSentTo,
                setCurrentTab,
                history,
                setIsGeneralMetadataEditMode,
                setIsLocalizedMetadataEditMode,
                setIsTerritorialMetadataEditMode
            )}
            <AppBar position="absolute" className="qc-app-bar">
                <Tabs value={currentTab} onChange={onHandleChange}>
                    {contentType !== 'series' && (
                        <Tab
                            className="qc-tools__tab"
                            label={TabsEnum.visualQc}
                            value={TabsEnum.visualQc}
                        />
                    )}
                    <Tab className="qc-tools__tab hidden" value={TabsEnum.none} />
                    <Tab
                        className="qc-tools__tab"
                        label={TabsEnum.metadata}
                        value={TabsEnum.metadata}
                    />
                    <Tab
                        className="qc-tools__tab"
                        label={TabsEnum.artwork}
                        value={TabsEnum.artwork}
                    />
                    <Divider />
                    {contentType !== 'series' && (
                        <Tab
                            className="qc-tools__tab"
                            label={TabsEnum.rights}
                            value={TabsEnum.rights}
                        />
                    )}
                    {contentType === 'series' && (
                        <Tab
                            className="qc-tools__tab"
                            label={TabsEnum.titles}
                            value={TabsEnum.titles}
                        />
                    )}
                    <Tab
                        className="qc-tools__tab"
                        label={TabsEnum.workHistory.replace('_', ' ')}
                        value={TabsEnum.workHistory}
                    />
                </Tabs>
                {getJobDeliveryButton(
                    onClickAddToDeliveryQueue,
                    contentItemViewMetadataDraft
                )}
            </AppBar>
            <div className="qc-tools">
                <TabContext value={currentTab}>
                    <>
                        {contentType !== 'series' && (
                            <div
                                className={classNames('video-audio-qc-tab-wrapper', {
                                    hidden: !isVisualQcCurrentTab,
                                })}
                            >
                                <VideoAudioQC
                                    playerRef={playerRef}
                                    timelineRef={timelineRef}
                                    item={item}
                                    routeId={id}
                                    jobDeliveryDate={jobDeliveryDate}
                                />
                            </div>
                        )}

                        <TabPanel value={TabsEnum.workHistory}>
                            <ContentItemViewWorkHistoryContainer id={id} />
                        </TabPanel>

                        {contentType !== 'series' && (
                            <TabPanel value={TabsEnum.rights}>
                                <ContentItemViewPublishingRightsContainer />
                            </TabPanel>
                        )}
                    </>
                    <TabPanel value={TabsEnum.none} />
                    <TabPanel value={TabsEnum.metadata}>
                        <ContentItemViewMetadata
                            jobDeliveryDate={jobDeliveryDate}
                            reloadRef={reloadRef}
                            isGeneralMetadataEditMode={isGeneralMetadataEditMode}
                            setIsGeneralMetadataEditMode={setIsGeneralMetadataEditMode}
                            isLocalizedMetadataEditMode={isLocalizedMetadataEditMode}
                            setIsLocalizedMetadataEditMode={
                                setIsLocalizedMetadataEditMode
                            }
                            isTerritorialMetadataEditMode={isTerritorialMetadataEditMode}
                            setIsTerritorialMetadataEditMode={
                                setIsTerritorialMetadataEditMode
                            }
                            contentItemViewMetadataDraft={contentItemViewMetadataDraft}
                        />
                    </TabPanel>
                    <TabPanel value={TabsEnum.artwork}>
                        <ContentItemViewArtwork jobDeliveryDate={jobDeliveryDate} />
                    </TabPanel>
                    {contentType === 'series' && (
                        <TabPanel value={TabsEnum.titles}>
                            <QCTitleContainer id={id} />
                        </TabPanel>
                    )}
                </TabContext>
            </div>
        </div>
    );
}
